import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  CheckCircle2,
  Eye,
  EyeOff,
  LoaderCircle,
  LockKeyhole,
  Mail,
  Phone,
  User,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

import ultrafastLogo from "../../assests/ultrafast_logo.png";
import { ChargerScene3D } from "../3D-Components";
import api from "../interceptors";
import "../LoginPage/index.css";

export default function RegisterPage() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    contact: "",
    password: "",
    confirmPassword: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState("");
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const passwordChecks = {
    length: formData.password.length >= 8,
    uppercase: /[A-Z]/.test(formData.password),
    number: /\d/.test(formData.password),
    special: /[!@#$%^&*]/.test(formData.password),
  };

  const passwordIsValid = Object.values(passwordChecks).every(Boolean);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setIsSubmitting(true);

    if (!passwordIsValid) {
      setMessage("Password does not meet requirements.");
      setIsSubmitting(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setMessage("Passwords do not match.");
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await api.post("/api/registerUser", formData);

      if (response.status >= 200 && response.status < 300) {
        setSuccess(true);
        setMessage("Registered successfully.");
        setTimeout(() => navigate("/login"), 900);
      }
    } catch (err) {
      console.error(err);
      if (err.response?.status === 409) {
        setMessage("User already exists. Please login instead.");
      } else {
        setMessage(err.response?.data?.message || "Registration failed.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="auth-shell">
      <section className="auth-visual" aria-label="Ultrafast Chargers">
        <div className="auth-visual-inner">
          <img
            src={ultrafastLogo}
            alt="Ultrafast Chargers"
            className="auth-logo"
          />
          <p className="auth-kicker">Ultrafast Chargers</p>
          <h2>Set up your charge ops account.</h2>
          <div className="auth-scene-wrap">
            <ChargerScene3D className="auth-scene" variant="auth" />
          </div>
          <div className="auth-stat-row">
            <span>Station access</span>
            <span>Live monitoring</span>
            <span>Protected login</span>
          </div>
        </div>
      </section>

      <section className="auth-panel">
        <motion.div
          className="auth-card"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45 }}
        >
          <div className="auth-heading">
            <p>Cloud dashboard</p>
            <h1>Create account</h1>
          </div>

          <form onSubmit={handleSubmit} className="auth-form">
            <label className="auth-field">
              <span>Username</span>
              <div className="auth-input-wrap">
                <User size={18} />
                <input
                  type="text"
                  name="username"
                  required
                  value={formData.username}
                  onChange={handleChange}
                  className="auth-input"
                />
              </div>
            </label>

            <label className="auth-field">
              <span>Email</span>
              <div className="auth-input-wrap">
                <Mail size={18} />
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  className="auth-input"
                />
              </div>
            </label>

            <label className="auth-field">
              <span>Contact No</span>
              <div className="auth-input-wrap">
                <Phone size={18} />
                <input
                  type="text"
                  name="contact"
                  required
                  value={formData.contact}
                  onChange={handleChange}
                  className="auth-input"
                />
              </div>
            </label>

            <label className="auth-field">
              <span>Password</span>
              <div className="auth-input-wrap">
                <LockKeyhole size={18} />
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  required
                  value={formData.password}
                  onChange={handleChange}
                  onBlur={() => setPasswordTouched(true)}
                  onFocus={() => setPasswordTouched(false)}
                  className="auth-input"
                />
                <button
                  type="button"
                  className="auth-visibility-button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  onClick={() => setShowPassword((value) => !value)}
                >
                  {showPassword ? <EyeOff size={19} /> : <Eye size={19} />}
                </button>
              </div>
            </label>

            {passwordTouched && !passwordIsValid && (
              <ul className="password-checks">
                <li className={passwordChecks.length ? "is-valid" : "is-invalid"}>
                  At least 8 characters
                </li>
                <li
                  className={passwordChecks.uppercase ? "is-valid" : "is-invalid"}
                >
                  At least one uppercase letter
                </li>
                <li className={passwordChecks.number ? "is-valid" : "is-invalid"}>
                  At least one number
                </li>
                <li
                  className={passwordChecks.special ? "is-valid" : "is-invalid"}
                >
                  At least one special character (!@#$%^&*)
                </li>
              </ul>
            )}

            <label className="auth-field">
              <span>Confirm Password</span>
              <div className="auth-input-wrap">
                <LockKeyhole size={18} />
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  required
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="auth-input"
                />
                <button
                  type="button"
                  className="auth-visibility-button"
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                  onClick={() => setShowConfirmPassword((value) => !value)}
                >
                  {showConfirmPassword ? (
                    <EyeOff size={19} />
                  ) : (
                    <Eye size={19} />
                  )}
                </button>
              </div>
            </label>

            <button
              type="submit"
              disabled={isSubmitting || success}
              className="auth-primary-button"
            >
              {success ? (
                <>
                  <CheckCircle2 size={18} />
                  Registered
                </>
              ) : isSubmitting ? (
                <>
                  <LoaderCircle size={18} className="animate-spin" />
                  Registering
                </>
              ) : (
                "Register"
              )}
            </button>
          </form>

          <Link to="/login" className="auth-link">
            Already have an account? Login
          </Link>

          {message && <p className="auth-message">{message}</p>}
        </motion.div>
      </section>
    </main>
  );
}
